import { IKgModelCard } from 'app/extreport/concierge/answering/kgModelCardTypes';
import { RowStatus } from './types';

// The pair's own state, before the run overlays the row shows; the banner reads this one.
export function deriveReviewStatus(card: IKgModelCard): RowStatus {
    const reg = card.registration_status;
    if (card.source_removed) return 'source_removed';
    if (card.source_retired) return 'source_missing';
    if (card.node_id === null && reg !== 'not_added') return 'removed_from_graph';
    if (reg === 'not_added') return 'not_added';
    if (reg === 'removed') return 'removed';
    if (reg === 'profiling') return 'processing';
    if (reg === 'published' && card.enabled_in_concierge) return 'published';
    return card.open_item_count > 0 || card.publish_blocked ? 'needs_review' : 'ready_to_publish';
}

// The KG editor's precedence for an unpublished pair (reportRowState): a run in flight reads Processing,
// then a failed last run reads Failed, over the review state, so both surfaces show the same word.
const RUN_OVERLAID: RowStatus[] = ['needs_review', 'ready_to_publish', 'removed', 'not_added'];

export function deriveRowStatus(card: IKgModelCard): RowStatus {
    const status = deriveReviewStatus(card);
    if (card.registration_status === 'published' || !RUN_OVERLAID.includes(status)) return status;
    if (card.active_run_uid) return 'processing';
    if (card.last_run_failure) return 'failed';
    return status;
}

// Processing already renders its own spinner, so the run indicator would only repeat it.
const RUN_STATUSES: RowStatus[] = ['removed', 'needs_review', 'ready_to_publish', 'published'];

export const rowRunning = (card: IKgModelCard, status: RowStatus) =>
    !!card.active_run_uid && RUN_STATUSES.includes(status);

const TEST_STATUSES: RowStatus[] = ['published', 'ready_to_publish', 'needs_review'];

export const testOffered = (status: RowStatus) => TEST_STATUSES.includes(status);

const ACTION_KEYS: Record<RowStatus, string> = {
    source_removed: '',
    source_missing: 'action_view_details',
    removed_from_graph: 'action_add_back',
    not_added: 'action_add',
    removed: 'action_add_back',
    processing: 'action_view_progress',
    failed: 'action_view_details',
    needs_review: 'action_review_items',
    ready_to_publish: 'action_review_publish',
    published: 'action_view',
};

export const rowActionKey = (status: RowStatus): string => ACTION_KEYS[status];

export const rowActionAdds = (status: RowStatus): boolean =>
    status === 'not_added' || status === 'removed' || status === 'removed_from_graph';

export const rowActionIcon = (status: RowStatus): 'new' | 'knowledge-graph-monitor' => {
    return rowActionAdds(status) ? 'new' : 'knowledge-graph-monitor';
};

export type { RowStatus };
